import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiUrl, assetUrl } from '../config/api';
import Pagination, { DEFAULT_PAGE_SIZE, pagingParams } from '../components/Pagination';
import SelfieImage from '../components/SelfieImage';
import { AGE_CATEGORY_LABELS } from '../constants/registration';
import { apiFetch, getSessionStatus } from '../utils/session';
import './HelpRequest.css';
import './Community.css';

function wingLabel(value) {
  if (!value) return '';
  return AGE_CATEGORY_LABELS[value] || value;
}

function MemberCard({ member, to, detailed, onRequest, requesting }) {
  const initial = member.name?.[0] || '?';
  const Tag = to ? Link : 'article';
  return (
    <Tag className="community-card" {...(to ? { to } : {})}>
      <SelfieImage
        userId={member.id}
        hasPhoto={Boolean(member.selfiePath)}
        alt=""
        className="community-avatar"
        placeholder={
          <div className="community-avatar placeholder" aria-hidden>
            {initial}
          </div>
        }
      />
      <div className="community-card-main">
        <h2 className="community-name">
          {member.name || 'Member'}
          {member.isYou ? <span className="community-you">You</span> : null}
        </h2>
        <p className="help-meta">
          <span>{member.memberId || '—'}</span>
          {member.role ? ` · ${member.role}` : ''}
          {member.area ? ` · ${member.area}` : ''}
        </p>
        {wingLabel(member.volunteeringCategory) ? (
          <p className="help-meta">{wingLabel(member.volunteeringCategory)}</p>
        ) : null}
        {member.jobBusiness ? <p className="help-meta">Current work: {member.jobBusiness}</p> : null}
        {member.availability ? <p className="help-meta">{member.availability}</p> : null}
        {member.showcase?.title ? <p className="help-meta">Talent: {member.showcase.title}</p> : null}
        {detailed && member.showcase?.description ? <p className="help-body">{member.showcase.description}</p> : null}
        {member.showcase?.photos?.length ? (
          <div className="community-photos">
            {member.showcase.photos.map((photo) => (
              <img key={photo} src={assetUrl(photo)} alt="" />
            ))}
          </div>
        ) : null}
        {detailed && member.showcase && !member.isYou ? (
          <button type="button" className="help-btn primary" disabled={requesting} onClick={() => onRequest(member)}>
            {requesting ? 'Sending…' : 'Request this person'}
          </button>
        ) : null}
        {member.skills?.length ? (
          <div className="community-chips">
            {member.skills.map((skill) => (
              <span key={skill} className="community-chip">
                {skill}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </Tag>
  );
}

function AnnouncementBlock({ title, announcement }) {
  if (!announcement?.message) return null;
  return (
    <section className="community-announce">
      <h2>{title}</h2>
      <p className="help-body">{announcement.message}</p>
      <p className="help-meta">
        {announcement.authorName || 'Sangram Sena'}
        {announcement.updatedAt
          ? ` · ${new Date(announcement.updatedAt).toLocaleString('en-IN', {
              day: 'numeric',
              month: 'short',
              hour: 'numeric',
              minute: '2-digit',
            })}`
          : ''}
      </p>
    </section>
  );
}

export default function Community() {
  const { memberId } = useParams();
  const [area, setArea] = useState('');
  const [members, setMembers] = useState([]);
  const [oneMember, setOneMember] = useState(null);
  const [announcement, setAnnouncement] = useState(null);
  const [cityAnnouncement, setCityAnnouncement] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const role = sessionStorage.getItem('sangramUserRole');
  const approved = getSessionStatus() === 'Active';
  const canPost = role === 'Admin' || role === 'Area Head';

  const load = useCallback(async () => {
    setError('');
    if (memberId) {
      const res = await apiFetch(apiUrl(`/community/members/${encodeURIComponent(memberId)}`));
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not load this member');
      setOneMember(data.member);
      setArea(data.member?.area || '');
      return;
    }
    const res = await apiFetch(apiUrl(`/community?${pagingParams(page, pageSize)}`));
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Could not load your community');
    setArea(data.area || '');
    setMembers(data.members || []);
    setAnnouncement(data.announcement);
    setCityAnnouncement(data.cityAnnouncement);
    setTotal(data.total ?? (data.members || []).length);
  }, [memberId, page, pageSize]);

  useEffect(() => {
    if (!approved) {
      setLoading(false);
      return;
    }
    load()
      .catch((e) => setError(e.message || 'Could not load your community'))
      .finally(() => setLoading(false));
  }, [approved, load]);

  const requestPerson = async (member) => {
    setRequesting(true);
    setError('');
    setNotice('');
    try {
      const res = await apiFetch(apiUrl(`/talent/${encodeURIComponent(member.memberId)}/request`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Could not send this request');
        return;
      }
      setNotice('Request sent to the Area Head as employment help. Their phone stays on their profile.');
    } catch {
      setError('Network error. Try again.');
    } finally {
      setRequesting(false);
    }
  };

  const post = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const res = await apiFetch(apiUrl('/community/announcement'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Could not save announcement');
        return;
      }
      setMessage('');
      setNotice(role === 'Admin' ? 'Announcement sent to every area.' : 'Announcement updated for your area.');
      await load();
    } catch {
      setError('Network error. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="help-page">
      <div className="help-shell">
        <header className="help-header">
          <div>
            <h1 className="help-title">My community</h1>
            <p className="help-sub">
              {area
                ? `Verified members in ${area}. Phone numbers and addresses stay hidden.`
                : 'Verified members in your area. Phone numbers and addresses stay hidden.'}
            </p>
          </div>
          {memberId ? (
            <div className="help-header-actions">
              <Link className="help-link" to="/community">
                All members
              </Link>
            </div>
          ) : null}
        </header>

        {!approved ? (
          <p className="help-error" role="alert">
            My Community is available after your membership is approved.
          </p>
        ) : null}
        {error ? (
          <p className="help-error" role="alert">
            {error}
          </p>
        ) : null}
        {notice ? (
          <div className="help-notice" role="status">
            <p className="help-notice-text">{notice}</p>
            <button
              type="button"
              className="help-notice-dismiss"
              aria-label="Dismiss"
              onClick={() => setNotice('')}
            >
              ×
            </button>
          </div>
        ) : null}

        {approved && !memberId ? (
          <>
            <AnnouncementBlock title="From Sangram Sena" announcement={cityAnnouncement} />
            <AnnouncementBlock title={`From the ${area || 'area'} Area Head`} announcement={announcement} />
            {canPost ? (
              <form className="help-form" onSubmit={post}>
                <label className="help-field">
                  {role === 'Admin' ? 'Announcement for every area' : `Announcement for ${area || 'your area'}`}
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    maxLength={1000}
                    required
                  />
                </label>
                <p className="help-hint">Posting replaces the current announcement. Members cannot reply here.</p>
                <button type="submit" className="help-btn primary" disabled={saving}>
                  {saving ? 'Saving…' : 'Post announcement'}
                </button>
              </form>
            ) : null}
          </>
        ) : null}

        {loading ? <p className="help-muted">Loading…</p> : null}

        {approved && memberId && oneMember ? (
          <MemberCard member={oneMember} detailed onRequest={requestPerson} requesting={requesting} />
        ) : null}

        {approved && !memberId && !loading && members.length === 0 ? (
          <p className="help-muted">No verified members in this area yet.</p>
        ) : null}

        {approved && !memberId ? (
          <>
            <ul className="community-grid">
              {members.map((member) => (
                <li key={member.memberId || member.name}>
                  <MemberCard member={member} to={member.memberId ? `/community/${member.memberId}` : ''} />
                </li>
              ))}
            </ul>
            <Pagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          </>
        ) : null}
      </div>
    </div>
  );
}
